export type Lang = 'de' | 'en';

export interface Strings {
  langName: string;
  switchTo: string;
  nav: { privacy: string; imprint: string; allTools: string };
  hero: { title: string; subtitle: string; localBadge: string };
  ui: {
    tabText: string;
    tabFile: string;
    textLabel: string;
    textPlaceholder: string;
    dropTitle: string;
    dropHint: string;
    output: string;
    copy: string;
    copied: string;
  };
  seo: {
    aboutTitle: string;
    aboutBody: string;
    faqTitle: string;
    faq: { q: string; a: string }[];
  };
  footer: { rights: string; noStorage: string };
}

const de: Strings = {
  langName: 'Deutsch',
  switchTo: 'English',
  nav: { privacy: 'Datenschutz', imprint: 'Impressum', allTools: 'Alle Tools' },
  hero: {
    title: 'Hash-Generator',
    subtitle:
      'Berechne SHA-Prüfsummen von Text und Dateien direkt im Browser. Nichts wird hochgeladen oder gespeichert.',
    localBadge: '100 % lokal · kostenlos',
  },
  ui: {
    tabText: 'Text',
    tabFile: 'Datei',
    textLabel: 'Eingabe',
    textPlaceholder: 'Text eingeben…',
    dropTitle: 'Datei hierher ziehen',
    dropHint: 'oder klicken zum Auswählen — die Datei verlässt deinen Browser nicht',
    output: 'Prüfsummen',
    copy: 'Kopieren',
    copied: 'Kopiert',
  },
  seo: {
    aboutTitle: 'Hashes sicher und lokal berechnen',
    aboutBody:
      'Dieser Generator berechnet SHA-1, SHA-256, SHA-384 und SHA-512 ausschließlich lokal in deinem Browser über die Web Crypto API. Weder Text noch Dateien werden hochgeladen oder gespeichert.',
    faqTitle: 'Häufige Fragen',
    faq: [
      {
        q: 'Werden meine Dateien hochgeladen?',
        a: 'Nein. Die Datei wird lokal im Browser gelesen und gehasht. Es findet keine Übertragung statt.',
      },
      {
        q: 'Warum gibt es kein MD5?',
        a: 'MD5 gilt als kryptografisch gebrochen und wird von der Web Crypto API nicht unterstützt. Verwende SHA-256 oder stärker.',
      },
      {
        q: 'Wofür nutze ich Prüfsummen?',
        a: 'Zum Verifizieren von Downloads und Integrität von Dateien sowie für Signatur- und Abgleichszwecke.',
      },
    ],
  },
  footer: {
    rights: 'Kostenlose Tools von ecosquirrel.',
    noStorage: 'Keine serverseitige Speicherung. Alles läuft lokal im Browser.',
  },
};

const en: Strings = {
  langName: 'English',
  switchTo: 'Deutsch',
  nav: { privacy: 'Privacy', imprint: 'Imprint', allTools: 'All tools' },
  hero: {
    title: 'Hash Generator',
    subtitle:
      'Compute SHA checksums of text and files right in your browser. Nothing is uploaded or stored.',
    localBadge: '100% local · free',
  },
  ui: {
    tabText: 'Text',
    tabFile: 'File',
    textLabel: 'Input',
    textPlaceholder: 'Enter text…',
    dropTitle: 'Drop a file here',
    dropHint: 'or click to choose — the file never leaves your browser',
    output: 'Checksums',
    copy: 'Copy',
    copied: 'Copied',
  },
  seo: {
    aboutTitle: 'Compute hashes securely and locally',
    aboutBody:
      'This tool computes SHA-1, SHA-256, SHA-384 and SHA-512 exclusively in your browser via the Web Crypto API. Neither text nor files are uploaded or stored.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        q: 'Are my files uploaded?',
        a: 'No. The file is read and hashed locally in the browser. Nothing is transmitted.',
      },
      {
        q: 'Why is there no MD5?',
        a: 'MD5 is cryptographically broken and not supported by the Web Crypto API. Use SHA-256 or stronger.',
      },
      {
        q: 'What are checksums for?',
        a: 'Verifying downloads and file integrity, plus signing and comparison purposes.',
      },
    ],
  },
  footer: {
    rights: 'Free tools by ecosquirrel.',
    noStorage: 'No server-side storage. Everything runs locally in your browser.',
  },
};

export const STRINGS: Record<Lang, Strings> = { de, en };
